// src/pages/AgentPage/AgentSteps.tsx
// Имитация «работы агента»: анимированная последовательность шагов перед ответом.
// Чисто визуальный компонент — реальная генерация идёт через стриминг чата.
import { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { Check } from 'lucide-react';

export interface AgentStep {
  label: string;
}

export function buildAgentSteps(folderName: string, fileNames: string[]): AgentStep[] {
  const filesToRead = fileNames.slice(0, 2);
  return [
    { label: `Сканирую папку «${folderName}»…` },
    ...filesToRead.map((fileName) => ({ label: `Читаю ${fileName}…` })),
    { label: 'Формирую ответ…' },
  ];
}

const STEP_INTERVAL_MS = 850;

export default function AgentSteps({ steps, active }: { steps: AgentStep[]; active: boolean }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!active) {
      setCurrentIndex(0);
      return;
    }
    setCurrentIndex(0);
    const timer = setInterval(() => {
      setCurrentIndex((prev) => Math.min(prev + 1, steps.length - 1));
    }, STEP_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [active, steps]);

  if (!active) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, py: 0.5 }}>
      {steps.map((step, index) => {
        const isDone = index < currentIndex;
        const isCurrent = index === currentIndex;
        if (index > currentIndex) return null;
        return (
          <Box key={step.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isDone ? (
              <Check size={13} style={{ color: '#059669', flexShrink: 0 }} />
            ) : (
              <CircularProgress size={12} sx={{ color: 'var(--color-primary)', flexShrink: 0 }} />
            )}
            <Typography
              sx={{
                fontSize: 12,
                color: isCurrent ? 'var(--color-text-secondary)' : 'var(--color-text-muted)',
                fontWeight: isCurrent ? 600 : 500,
              }}
            >
              {step.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
